import { Palette, TriangleAlert } from 'lucide-react';

import {
  consistencySeverity,
  describeConsistencyFindings,
  type SectionConsistency,
} from './sectionConsistency';

/**
 * Per-section marker for cross-form design consistency (EMR-1410).
 *
 * Sits alongside `SectionLayoutStatusBadge` rather than folding into it: that
 * badge answers "does this section have a valid custom layout", this one
 * answers "is this section designed the same way as the other forms that share
 * its questions". A section can easily be green on one and red on the other.
 */
export function SectionConsistencyBadge({
  state,
}: {
  state: SectionConsistency;
}) {
  if (state.kind === 'unchecked') {
    // Shown muted rather than omitted: "not checked" and "consistent" are
    // different answers, and a missing marker would imply the stronger one.
    return (
      <span
        className='inline-flex items-center text-muted-foreground/40'
        title={`Design consistency not checked — ${state.reason}`}
      >
        <Palette className='h-3.5 w-3.5' aria-hidden />
      </span>
    );
  }

  if (state.kind === 'unrecognised') {
    // Loud on purpose: this is a data problem, not a clean or unknown result.
    return (
      <span
        className='inline-flex items-center text-destructive'
        title={`Not comparable — ${state.reason}`}
      >
        <TriangleAlert className='h-3.5 w-3.5' aria-hidden />
      </span>
    );
  }

  if (state.kind === 'consistent') {
    return (
      <span
        className='inline-flex items-center text-green-700'
        title='Matches the other forms that share these questions'
      >
        <Palette className='h-3.5 w-3.5' aria-hidden />
      </span>
    );
  }

  return (
    <span
      className={
        consistencySeverity(state.findings) === 'error'
          ? 'inline-flex items-center text-destructive'
          : 'inline-flex items-center text-amber-600'
      }
      title={describeConsistencyFindings(state.findings)}
    >
      <Palette className='h-3.5 w-3.5' aria-hidden />
    </span>
  );
}
